import { $size } from '@/utils/cssVarUtils';
import { Checkbox } from '@/components/forms/Checkbox';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Table as ChakraTable, Thead, Tbody, Tr, Th, Td, TableCaption, TableContainer, TableProps, TableCaptionProps, Stack, Box, useTheme } from '@chakra-ui/react'
import { CSSProperties, useCallback, useEffect } from 'react';
import { Column, ColumnInstance, HeaderGroup, Row, useRowSelect, useSortBy, useTable, TableInstance, TableState, useColumnOrder, usePagination } from 'react-table';
import Pagination, { PaginationProps } from '../Pagination/component';
interface ICaptionProps extends TableCaptionProps {
  title: string
}

interface ITableState extends TableState {
  selectedRowIds?: any,
  pageIndex?: number,
  pageSize?: number,

}
interface ITable extends TableInstance {
  selectedFlatRows?: Row[],
  state: ITableState,
  page?: Row[],
  setPageSize?: Function,
  gotoPage?: Function
}

interface ITableProps<T extends object> extends TableProps {
  columns: readonly Column<object>[]
  caption?: ICaptionProps
  width?: string | number
  data: T[],
  bgColor?: string,
  style?: CSSProperties
  onRowSelectChange?: (rows: Row[]) => void
  tableProps?: any
  headerProps?: any
  rowProps?: any
  columnProps?: any
  colorScheme?: string
  showIndex?: boolean,
  pagination?: boolean,
  paginationProps?: PaginationProps,
  getRowProps?: (row: Row) => object
}

export function Table<T extends object>({
  columns,
  data,
  caption,
  width,
  variant,
  size,
  maxWidth,
  display,
  overflowX,
  overflowY,
  whiteSpace,
  bgColor,
  style = {},
  onRowSelectChange,
  tableProps = {},
  headerProps = {},
  rowProps = {},
  columnProps = {},
  colorScheme,
  showIndex,
  pagination,
  paginationProps = { total: data.length },
  getRowProps = () => ({})
}: ITableProps<T>) {
  const theme = useTheme();

  const getHeaderProps = useCallback((header: HeaderGroup) => {
    return { key: header.id, width: header.width, minWidth: header.minWidth, maxWidth: header.maxWidth, ...headerProps }
  }, [])

  const combineGetRowProps = useCallback((row: Row) => {
    return { key: row.id + row.index, ...rowProps, ...getRowProps(row) };
  }, [])

  const getColumnProps = useCallback((col: ColumnInstance) => {
    const colProps = {
      key: col.id,
      width: col.width,
      style: (col as any).style,
      textAlign: (col as any).textAlign,
      maxWidth: col.maxWidth,
      minWidth: col.minWidth,
      ...columnProps
    }
    return colProps
  }, []);

  const tableInstance: ITable = useTable({
    columns, data, initialState: {}
  }, useSortBy, usePagination, useColumnOrder, useRowSelect, hooks => {
    if (!onRowSelectChange) {
      return
    }
    const allColumns = (columns: ColumnInstance<object>[]) => {
      const originalCols = [
        // make a column for selection
        {
          id: 'selection',
          width: 4,
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllRowsSelectedProps }: any) => {
            return (
              <Box>
                <Checkbox {...getToggleAllRowsSelectedProps()} indeterminate="" />
              </Box>
            )
          },
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }: { row: Row }) => (
            <Box>
              <Checkbox
                isChecked={(row as any).getToggleRowSelectedProps().checked}
                {...(row as any).getToggleRowSelectedProps()}
                isIndeterminate={false}
                indeterminate=""
              />
            </Box>
          ),
        },
        ...columns,
      ];
      if (showIndex) {
        originalCols.unshift({
          id: 'indexCol',
          width: 4,
          Header: () => {
            return (
              <Box w="1">
                Index
              </Box>
            )
          },
          Cell: ({ row }: { row: Row }) => (
            <Box w="4">
              {row.index + 1}
            </Box>
          ),
        });
      }
      return originalCols;
    }
    hooks.visibleColumns.push(col => allColumns(col))
  });

  const { getTableProps, getTableBodyProps, headerGroups, rows, page, setPageSize, gotoPage, prepareRow, selectedFlatRows } = tableInstance;

  useEffect(() => {
    onRowSelectChange?.(selectedFlatRows!)
  }, [selectedFlatRows]);

  const columnPropsGetter = useCallback((column: HeaderGroup<{}>) => {
    const allProps = [
      {
        className: (column as any).className,
        style: (column as any).style,
      },
      getColumnProps(column),
      getHeaderProps(column),
    ];
    if ((column as any).sorter) {
      allProps.unshift((column as any).getSortByToggleProps())
    }
    return column.getHeaderProps(allProps)
  }, [getColumnProps, getHeaderProps])

  const allRows = pagination ? page! : rows;

  return <TableContainer maxWidth={maxWidth} display={display} overflowX={overflowX} overflowY={overflowY} whiteSpace={whiteSpace}>
    <ChakraTable width={width ?? $size('max')} {...getTableProps()} colorScheme={colorScheme ?? `${theme.colors.scene.border};`} style={style} variant={variant} bgColor={bgColor} size={size} {...tableProps}>
      {caption && <TableCaption {...caption}>{caption.title}</TableCaption>}
      <Thead>
        {
          headerGroups.map(headerGroup =>
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {// Loop over the headers in each row
                headerGroup.headers.map(column => (
                  // Apply the header cell props
                  <Th
                    userSelect='none'
                    color='primary'
                    textTransform='none'
                    {...columnPropsGetter(column)}
                  >
                    {// Render the header
                      column.render('Header')}
                    <span style={{ position: 'absolute', marginLeft: 4 }}>
                      {(column as any).isSorted
                        ? (column as any).isSortedDesc
                          ? <ChevronDownIcon w={4} h={4} />
                          : <ChevronUpIcon w={4} h={4} />
                        : ''}
                    </span>
                  </Th>
                ))}
            </Tr>)
        }
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {// Loop over the table rows
          allRows.map(row => {
            // Prepare the row for display
            prepareRow(row)
            return (
              // Apply the row props
              <Tr {...row.getRowProps([
                combineGetRowProps(row)
              ])}>
                {// Loop over the rows cells
                  row.cells.map(cell => {
                    // Apply the cell props
                    return (
                      <Td {...cell.getCellProps([
                        getColumnProps(cell.column),
                      ])}>
                        {// Render the cell contents
                          cell.render('Cell')}
                      </Td>
                    )
                  })}
              </Tr>
            )
          })}
      </Tbody>
    </ChakraTable>
    <Stack marginTop="4" direction="row" align="center">
      <Box flex='1' />
      {pagination &&
        <Pagination
          onChange={p => gotoPage?.(p)}
          onPageSizeChange={size => setPageSize?.(size)}
          {...paginationProps}
        />}
    </Stack>
  </TableContainer>
}